import { Link } from "react-router-dom";
import { ArrowRight, Phone, MapPin, Check, Compass, ShieldCheck, Hammer, HeartHandshake, Home, Layers, Wrench, Building2 } from "lucide-react";
import Reveal from "@/components/Reveal";
import SEOHead from "@/components/SEOHead";
import { HERO_PHOTO, FEATURED_PHOTOS, ABOUT_PHOTO, HOME_INTRO_PHOTO, CRAFTSMANSHIP_PHOTO, photoDims } from "@/lib/photos";
import { track } from "@/lib/analytics";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";

const PROCESS = [
    {
        n: "01",
        title: "Initial Conversation",
        body: "We start with a straightforward call or meeting to hear what you're planning, your land, and the way your family lives.",
    },
    {
        n: "02",
        title: "Design & Planning",
        body: "We work through the sketch, the layout, and the details together so the home reflects your lifestyle before a single board is cut.",
    },
    {
        n: "03",
        title: "Site Work & Foundation",
        body: "Ground-up residential construction across northeast Oklahoma, coordinated by a single accountable builder.",
    },
    {
        n: "04",
        title: "Framing & Mechanicals",
        body: "Framing, mechanical systems, and rough-ins are handled by trusted local trades we stand behind.",
    },
    {
        n: "05",
        title: "Finishes & Craftsmanship",
        body: "Trim, cabinetry, and finish work are where a real custom home shows — we obsess over the details you'll touch every day.",
    },
    {
        n: "06",
        title: "Final Walkthrough",
        body: "We walk the finished home with you and hand over a house that's ready for the way you actually live.",
    },
];

const HOME_TYPES = [
    { Icon: Home, title: "Custom Homes", body: "One-of-a-kind homes designed and built around the way you live." },
    { Icon: Building2, title: "New Construction", body: "Full-scope ground-up residential builds across northeast Oklahoma." },
    { Icon: Layers, title: "Barndominiums", body: "Custom barndominium builds tailored to your land and lifestyle." },
    { Icon: Layers, title: "Additions", body: "Seamless square footage that looks and lives like part of the original home." },
    { Icon: Hammer, title: "Remodels", body: "Thoughtful transformations of the homes you already love." },
    { Icon: Wrench, title: "Detail Carpentry", body: "Trim, cabinetry, and millwork that make a home feel truly custom." },
];

const REASONS = [
    { Icon: ShieldCheck, title: "One Builder, One Promise", body: "One point of contact from the first sketch to the final walkthrough — no hand-offs, no hiding behind change orders." },
    { Icon: Hammer, title: "Real Craftsmanship", body: "We sweat the reveals, transitions, and finish work — the parts of a home you touch every day." },
    { Icon: Compass, title: "Local & Rooted", body: "Born and built in Tahlequah, Oklahoma. We work with trades we know and stand behind." },
    { Icon: HeartHandshake, title: "Honest Process", body: "Clear communication and transparent scheduling — you always know where things stand." },
];

const FAQS = [
    {
        q: "Where does Harris Homes build?",
        a: "Harris Homes builds in Tahlequah, Oklahoma and throughout northeast Oklahoma.",
    },
    {
        q: "What types of homes do you build?",
        a: "Custom homes, new construction, barndominiums, additions, and full remodels.",
    },
    {
        q: "Can you build on my land?",
        a: "Yes. We handle full-scope residential construction from site work through finish, coordinated by a single accountable builder.",
    },
    {
        q: "Who runs my project?",
        a: "Every project is led personally by Harris Homes from the first sketch to the final walkthrough — one builder, one point of contact.",
    },
    {
        q: "How is my project priced and scheduled?",
        a: "We work through honest budgets and clear timelines together before construction starts, and keep communication open throughout the build.",
    },
    {
        q: "How do I start a project?",
        a: "Send a note through the contact form or call (918) 373-4341. We take a limited number of projects each year — reach out early, even if you're still in the planning stage.",
    },
];

const GALLERY = [
    HERO_PHOTO,
    HOME_INTRO_PHOTO,
    ...FEATURED_PHOTOS.slice(1, 5),
    ABOUT_PHOTO,
    CRAFTSMANSHIP_PHOTO,
];

export default function CustomHomeBuilderTahlequah() {
    return (
        <div data-testid="custom-home-tahlequah-page">
            <SEOHead
                title="Build a Custom Home on Your Land in Tahlequah, OK"
                description="Own land near Tahlequah? Harris Homes designs and builds your custom home on it, from site walk to move-in. Send your lot details for a quote."
                canonical="https://harrishomesok.com/custom-home-builder-tahlequah-ok"
            />

            {/* Header */}
            <section className="pt-20 md:pt-32 pb-14 md:pb-20 px-6 md:px-10">
                <div className="max-w-[1480px] mx-auto grid md:grid-cols-12 gap-12">
                    <Reveal className="md:col-span-7">
                        <div className="overline mb-6">
                            <MapPin size={11} strokeWidth={1.5} className="inline mr-2 -mt-0.5" style={{ color: "var(--hh-gold)" }} />
                            Tahlequah · Northeast Oklahoma
                        </div>
                        <h1 className="font-display text-5xl sm:text-7xl md:text-[7rem] leading-[0.95] tracking-tight max-w-4xl">
                            Build Your Custom Home on Your Land
                            <br />
                            <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}>
                                in Tahlequah
                            </span>
                        </h1>
                        <p className="mt-10 max-w-2xl text-base md:text-lg leading-relaxed" style={{ color: "var(--hh-text-dim)" }}>
                            Own land near Tahlequah, or about to buy it? We start with a walk
                            of your lot — access, slope, septic or water, and site prep — then
                            handle Cherokee County permits and full-scope construction, from
                            foundation to finishes, with one accountable builder. You see honest
                            numbers before work begins, so the home fits your land and your budget.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link to="/contact" data-testid="tahlequah-hero-cta" className="btn-gold">
                                Start Your Project <ArrowRight size={14} />
                            </Link>
                            <a href="tel:+19183734341" data-testid="tahlequah-hero-phone" className="btn-ghost">
                                <Phone size={14} strokeWidth={1.5} /> (918) 373-4341
                            </a>
                        </div>
                    </Reveal>
                    <Reveal delay={0.15} className="md:col-span-5">
                        <div className="relative img-zoom">
                            <img
                                src={HERO_PHOTO}
                                alt="A custom Harris Homes residence in Tahlequah, Oklahoma"
                                width={photoDims(HERO_PHOTO)?.w}
                                height={photoDims(HERO_PHOTO)?.h}
                                className="w-full h-[520px] object-cover"
                                loading="eager"
                                fetchPriority="high"
                            />
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Intro / About strip */}
            <section className="py-20 md:py-28 px-6 md:px-10" style={{ background: "var(--hh-bg-2)" }}>
                <div className="max-w-[1100px] mx-auto text-center">
                    <Reveal>
                        <div className="overline mb-5">Custom Home Building</div>
                        <h2 className="font-display text-4xl md:text-5xl leading-[1.1] tracking-tight">
                            A home built for the way
                            <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}> your family lives.</span>
                        </h2>
                        <p className="mt-8 max-w-2xl mx-auto text-base md:text-lg leading-relaxed" style={{ color: "var(--hh-text-dim)" }}>
                            From the first sketch to the final walkthrough, we partner
                            closely with each family to build a one-of-a-kind home that
                            reflects their land, their lifestyle, and the quality they
                            expect for decades to come.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* Process */}
            <section className="py-24 md:py-32 px-6 md:px-10">
                <div className="max-w-[1480px] mx-auto">
                    <Reveal>
                        <div className="overline mb-5">Our Process</div>
                        <h2 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-3xl">
                            How a Harris Homes project comes together.
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid md:grid-cols-2 gap-10 md:gap-14">
                        {PROCESS.map((step, i) => (
                            <Reveal key={step.n} delay={i * 0.05}>
                                <div data-testid={`process-step-${step.n}`} className="grid grid-cols-12 gap-5">
                                    <div className="col-span-2">
                                        <div className="font-display text-5xl md:text-6xl" style={{ color: "var(--hh-gold)" }}>
                                            {step.n}
                                        </div>
                                    </div>
                                    <div className="col-span-10">
                                        <h3 className="font-display text-2xl md:text-3xl mb-3 leading-tight">
                                            {step.title}
                                        </h3>
                                        <p className="text-sm md:text-base leading-relaxed" style={{ color: "var(--hh-text-dim)" }}>
                                            {step.body}
                                        </p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Home types */}
            <section className="py-24 md:py-32 px-6 md:px-10" style={{ background: "var(--hh-bg-2)" }}>
                <div className="max-w-[1480px] mx-auto">
                    <Reveal>
                        <div className="overline mb-5">Types of Homes We Build</div>
                        <h2 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-3xl">
                            Custom projects,
                            <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}> personally delivered.</span>
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-px" style={{ background: "var(--hh-border)" }}>
                        {HOME_TYPES.map((t, i) => (
                            <Reveal key={t.title} delay={i * 0.05}>
                                <div className="h-full p-8 md:p-10" style={{ background: "var(--hh-bg-2)" }} data-testid={`home-type-${i}`}>
                                    <t.Icon size={28} strokeWidth={1.2} style={{ color: "var(--hh-gold)" }} />
                                    <h3 className="font-display text-2xl md:text-3xl mt-6 mb-3 leading-tight">
                                        {t.title}
                                    </h3>
                                    <p className="text-sm leading-relaxed" style={{ color: "var(--hh-text-dim)" }}>
                                        {t.body}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Build on your land */}
            <section className="py-24 md:py-32 px-6 md:px-10">
                <div className="max-w-[1480px] mx-auto grid md:grid-cols-12 gap-12 md:gap-16 items-center">
                    <Reveal className="md:col-span-5">
                        <div className="relative img-zoom">
                            <img
                                src={ABOUT_PHOTO}
                                alt="A Harris Homes residence built on the owner's land in northeast Oklahoma"
                                width={photoDims(ABOUT_PHOTO)?.w}
                                height={photoDims(ABOUT_PHOTO)?.h}
                                className="w-full h-[520px] object-cover"
                                loading="lazy"
                            />
                        </div>
                    </Reveal>
                    <Reveal delay={0.1} className="md:col-span-7">
                        <div className="overline mb-5">Build on Your Land</div>
                        <h2 className="font-display text-4xl md:text-5xl leading-[1.1] tracking-tight max-w-xl">
                            We build on your land
                            <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}> across northeast Oklahoma.</span>
                        </h2>
                        <div className="gold-rule w-24 my-8" />
                        <p className="text-base md:text-lg leading-relaxed max-w-xl" style={{ color: "var(--hh-text-dim)" }}>
                            Have a lot you already own or a plot you're ready to purchase?
                            We handle full-scope residential construction — site work,
                            foundation, framing, mechanicals, finishes — all coordinated by
                            a single accountable builder.
                        </p>
                        <ul className="mt-8 space-y-3 max-w-md">
                            {[
                                "Tahlequah and surrounding northeast Oklahoma",
                                "Full site work through final finish",
                                "One point of contact from start to keys",
                                "Trusted local trades we stand behind",
                            ].map((item) => (
                                <li key={item} className="flex items-start gap-3 text-sm" style={{ color: "var(--hh-text)" }}>
                                    <Check size={16} strokeWidth={1.6} style={{ color: "var(--hh-gold)", marginTop: 2 }} />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-10">
                            <Link to="/contact" data-testid="tahlequah-land-cta" onClick={() => track.ctaClick("Talk About Your Land", "tahlequah-land")} className="btn-gold">
                                Talk About Your Land <ArrowRight size={14} />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Completed project photos */}
            <section className="py-20 md:py-28 px-6 md:px-10" style={{ background: "var(--hh-bg-2)" }}>
                <div className="max-w-[1480px] mx-auto">
                    <Reveal>
                        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                            <div>
                                <div className="overline mb-5">Completed Projects</div>
                                <h2 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-2xl">
                                    A look at homes we've built by hand.
                                </h2>
                            </div>
                            <Link to="/projects" data-testid="tahlequah-projects-link" className="btn-ghost self-start">
                                See Full Gallery <ArrowRight size={14} />
                            </Link>
                        </div>
                    </Reveal>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                        {GALLERY.slice(0, 8).map((src, i) => (
                            <Reveal key={src + i} delay={Math.min(i * 0.04, 0.32)}>
                                <div className="relative img-zoom h-[220px] md:h-[280px]">
                                    <img src={src} alt="A Harris Homes completed project in Tahlequah, OK" width={photoDims(src)?.w} height={photoDims(src)?.h} className="w-full h-full object-cover" loading="lazy" />
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Reasons to choose */}
            <section className="py-24 md:py-32 px-6 md:px-10">
                <div className="max-w-[1480px] mx-auto">
                    <Reveal>
                        <div className="overline mb-5">Why Harris Homes</div>
                        <h2 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-3xl">
                            Reasons families choose us to build their home.
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid md:grid-cols-2 gap-10 md:gap-14">
                        {REASONS.map((r, i) => (
                            <Reveal key={r.title} delay={i * 0.06}>
                                <div className="flex gap-5 items-start" data-testid={`reason-${i}`}>
                                    <r.Icon size={28} strokeWidth={1.2} style={{ color: "var(--hh-gold)" }} />
                                    <div>
                                        <h3 className="font-display text-2xl md:text-3xl mb-2 leading-tight">
                                            {r.title}
                                        </h3>
                                        <p className="text-sm md:text-base leading-relaxed" style={{ color: "var(--hh-text-dim)" }}>
                                            {r.body}
                                        </p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-24 md:py-32 px-6 md:px-10" style={{ background: "var(--hh-bg-2)" }}>
                <div className="max-w-[1100px] mx-auto">
                    <Reveal>
                        <div className="overline mb-5">Frequently Asked</div>
                        <h2 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight">
                            Answers before we
                            <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}> even meet.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <Accordion type="single" collapsible className="mt-12" data-testid="tahlequah-faq">
                            {FAQS.map((f, i) => (
                                <AccordionItem
                                    key={f.q}
                                    value={`faq-${i}`}
                                    data-testid={`faq-item-${i}`}
                                    className="border-b"
                                    style={{ borderColor: "var(--hh-border)" }}
                                >
                                    <AccordionTrigger className="font-display text-xl md:text-2xl text-left py-6 hover:no-underline">
                                        {f.q}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-base leading-relaxed pb-6" style={{ color: "var(--hh-text-dim)" }}>
                                        {f.a}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </Reveal>
                </div>
            </section>

            {/* CTA */}
            <section className="py-28 md:py-40 px-6 md:px-10 text-center">
                <Reveal>
                    <div className="overline mb-5">Free Consultation</div>
                    <h2 className="font-display text-4xl md:text-6xl leading-tight tracking-tight max-w-3xl mx-auto">
                        Ready to build in
                        <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}> Tahlequah?</span>
                    </h2>
                    <p className="mt-6 max-w-xl mx-auto text-base md:text-lg leading-relaxed" style={{ color: "var(--hh-text-dim)" }}>
                        Send us a note — even if you're still in the dreaming stage. We
                        take a limited number of projects each year and reply within one
                        business day.
                    </p>
                    <div className="mt-10 flex flex-wrap gap-4 justify-center">
                        <Link to="/contact" data-testid="tahlequah-final-cta" onClick={() => track.ctaClick("Start a Conversation", "tahlequah-final")} className="btn-gold">
                            Start a Conversation <ArrowRight size={14} />
                        </Link>
                        <a href="tel:+19183734341" data-testid="tahlequah-final-phone" onClick={() => track.phoneClick("tahlequah-final")} className="btn-ghost">
                            <Phone size={14} /> (918) 373-4341
                        </a>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
